import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Upload, Trash2, Loader2, Download, BookOpen } from 'lucide-react';

export const RegoleCompagnie = ({ isAdmin }) => {
  const [docs, setDocs] = useState([]);
  const [compagnia, setCompagnia] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get('/ai-admin/compagnie-docs');
      setDocs(r.data.docs || []);
    } catch { toast.error('Errore caricamento documenti'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!compagnia.trim()) { toast.error('Indica la compagnia'); return; }
    if (!file) { toast.error('Seleziona un file PDF'); return; }
    const fd = new FormData();
    fd.append('compagnia', compagnia.trim());
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await api.post('/ai-admin/compagnie-docs', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      if (r.data.estratto_ok) toast.success(`Caricato: ${r.data.chars} caratteri estratti. Sofia userà queste regole.`);
      else toast.warning('Caricato, ma poco testo estratto (documento scansionato?). Verifica il contenuto.');
      setCompagnia(''); setFile(null);
      document.getElementById('regole-file-input').value = '';
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento');
    } finally { setUploading(false); }
  };

  const elimina = async (id) => {
    if (!window.confirm('Eliminare questo documento di regole?')) return;
    try {
      await api.delete(`/ai-admin/compagnie-docs/${id}`);
      toast.success('Documento eliminato'); load();
    } catch { toast.error('Errore eliminazione'); }
  };

  const scarica = async (d) => {
    try {
      const r = await api.get(`/ai-admin/compagnie-docs/${d.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = d.filename || 'regole.pdf'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('File non disponibile'); }
  };

  return (
    <div className="space-y-6" data-testid="regole-compagnie">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen size={18} className="text-indigo-600" /> Regole Compagnie (PDF)
          </CardTitle>
          <p className="text-sm text-slate-500">
            Carica i PDF con le regole e le condizioni di ogni compagnia. Sofia li userà per rispondere ai clienti
            su come funziona una garanzia o come gestire una pratica. Puoi aggiornarli quando vuoi.
          </p>
        </CardHeader>
        {isAdmin && (
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Input
                placeholder="Nome compagnia (es. Allianz, Generali, UnipolSai...)"
                value={compagnia}
                onChange={(e) => setCompagnia(e.target.value)}
                data-testid="regole-compagnia-input"
              />
              <Input
                id="regole-file-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="regole-file-input"
                className="cursor-pointer"
              />
            </div>
            <Button onClick={upload} disabled={uploading} data-testid="regole-upload-btn" className="w-full sm:w-auto">
              {uploading ? <><Loader2 size={15} className="mr-2 animate-spin" /> Caricamento e lettura...</> : <><Upload size={15} className="mr-2" /> Carica regole</>}
            </Button>
          </CardContent>
        )}
      </Card>

      <div className="space-y-2">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : docs.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8">Nessun documento caricato. Carica il primo PDF di regole.</p>
        ) : (
          docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 rounded-lg border bg-white p-3" data-testid={`regole-doc-${d.id}`}>
              <FileText size={20} className="shrink-0 text-indigo-500" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{d.compagnia}</span>
                  <Badge className={d.chars > 120 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {d.chars > 120 ? `${d.chars} caratteri letti` : 'poco testo'}
                  </Badge>
                </div>
                <p className="truncate text-xs text-slate-400">{d.filename}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => scarica(d)} data-testid={`regole-download-${d.id}`}>
                <Download size={15} />
              </Button>
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => elimina(d.id)} data-testid={`regole-delete-${d.id}`} className="text-red-500 hover:text-red-600">
                  <Trash2 size={15} />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
